import { useDispatch } from "react-redux";
import {
  getPairMeta,
  getPairTicker,
} from "@components/pages/Futures/components/Quotes/pairMeta";
import { showRiskValidationError } from "@actions/futures/positionNotifications";
import { useTranslation } from "@helpers/translate";
import {
  LONG_COLOR,
  MUTED_COLOR,
  SHORT_COLOR,
} from "../constants";
import { fieldWidth, formatUsd } from "../utils/format";
import { getWagerError, getWagerErrorText } from "../utils/wagerValidation";
import { useLeverageInput } from "./useLeverageInput";
import { useRiskSettings } from "./useRiskSettings";
import { useWagerInput } from "./useWagerInput";

/**
 * Composes TradeForm UI state from Controlled props + local hooks.
 *
 * @param {object} props — same public TradeForm props from Controls
 */
export const useTradeForm = ({
  pair,
  direction,
  onDirectionChange,
  leverage,
  onLeverageChange,
  wager,
  onWagerChange,
  availableBalance,
  liquidationPrice,
  setTakeProfitPercent,
  setStopLossPercent,
  onPlaceTrade,
  isPlacingTrade = false,
  isBalanceLoading = false,
}) => {
  const dispatch = useDispatch();
  const { __ } = useTranslation();
  const isLong = direction !== "short";
  const pairMeta = getPairMeta(pair?.slug);
  const baseAsset = getPairTicker(pair?.slug);
  const directionColor = isLong ? LONG_COLOR : SHORT_COLOR;

  const wagerInput = useWagerInput({
    wager,
    onWagerChange,
    availableBalance,
  });

  const leverageInput = useLeverageInput({
    leverage,
    onLeverageChange,
  });

  const risk = useRiskSettings({
    wager,
    setTakeProfitPercent,
    setStopLossPercent,
  });

  // Errors wait for the wallets; the button stays locked regardless
  const wagerError = isBalanceLoading
    ? null
    : getWagerError(wager, availableBalance);

  const liquidationText = Number.isFinite(Number(liquidationPrice))
    ? formatUsd(liquidationPrice)
    : "—";

  const buildErrorText = (error) => {
    if (!error.textParams) return __(error.textKey);

    const { min, max, mode } = error.textParams;
    const fmt = (v) => (mode === "%" ? `${v}%` : `$${v}`);

    return __(error.textKey).replace("{min}", fmt(min)).replace("{max}", fmt(max));
  };

  const handlePlaceTrade = async (...args) => {
    const error = risk.getValidationError();

    if (error) {
      dispatch(
        showRiskValidationError({
          title: __(error.titleKey),
          text: buildErrorText(error),
        }),
      );
      return false;
    }

    return onPlaceTrade(...args);
  };

  return {
    isLong,
    pairMeta,
    baseAsset,
    directionColor,
    longColor: LONG_COLOR,
    shortColor: SHORT_COLOR,
    mutedColor: MUTED_COLOR,
    onDirectionChange,
    leverage,
    availableBalance,
    balanceText: formatUsd(availableBalance),
    liquidationText,
    onPlaceTrade: handlePlaceTrade,
    isPlacingTrade,
    fieldWidth,
    isWagerInvalid: Boolean(getWagerError(wager, availableBalance)),
    wager: {
      ...wagerInput,
      isError: Boolean(wagerError),
      errorText: getWagerErrorText(wagerError, __),
      wagerFieldWidth: fieldWidth(wagerInput.displayWager, "0.00"),
    },
    leverageField: {
      ...leverageInput,
      leverageFieldWidth: fieldWidth(leverageInput.leverageDraft, "10"),
    },
    risk,
  };
};
